import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { noop } from 'ui-components/default-props/noop';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Gap } from 'ui-components/components/flex/gap';
import type { GroupedNotification } from '../../../../store/notification-hub/notification-hub.types';
import { getDisplayCategoryConfig } from '../../../../store/notification-hub/notification-display-category';
import { NotificationItem } from '../notification-item';

import { messages } from './messages';
import style from './style.module.less';

type GroupedNotificationItemProps = {
  groupedNotification: GroupedNotification;
  expandedNotificationId?: string | null;
  onToggleExpand?: (notificationId: string) => void;
};

export const GroupedNotificationItem = ({
  groupedNotification,
  expandedNotificationId,
  onToggleExpand = noop,
}: GroupedNotificationItemProps) => {
  const intl = useIntl();
  const { notification, groupCount, groupedNotifications, isGrouped } = groupedNotification;
  const notificationId = String(notification.notificationID);
  const isExpanded = expandedNotificationId === notificationId;

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent | React.FormEvent) => {
      e.stopPropagation();
      onToggleExpand(notificationId);
    },
    [onToggleExpand, notificationId],
  );

  const handleContainerClick = useCallback(() => {
    onToggleExpand(notificationId);
  }, [onToggleExpand, notificationId]);

  if (!isGrouped) {
    return <NotificationItem notification={notification} />;
  }

  return (
    <div className={classnames(style['grouped-notification-container'], { [style.expanded]: isExpanded })} onClick={handleContainerClick}>
      <FlexRow className={style.row1} gap={Gap.Space4}>
        <Text className={style['group-count']} element='span' textType={TextType.Paragraph} value={`(${groupCount})`} />
        <Text
          className={style['category-label']}
          element='span'
          textType={TextType.Paragraph}
          value={intl.formatMessage(getDisplayCategoryConfig(notification.notificationType, notification.category).message)}
        />
      </FlexRow>

      <div className={style['group-items-container']}>
        <div className={style['group-items']}>
          {groupedNotifications.map((notif) => (
            <NotificationItem key={notif.notificationID} hideCategoryLabel isExpanded={isExpanded} isInGroup notification={notif} />
          ))}
        </div>
      </div>

      <FlexRow className={style['expand-button-container']}>
        <Button
          ariaLabel={intl.formatMessage(isExpanded ? messages.collapseGroup : messages.expandGroup)}
          className={style['expand-button']}
          onClick={handleToggleExpand}
          theme={THEME_CONSTANTS.unstyled}
        >
          <Text
            element='span'
            textType={TextType.Paragraph}
            value={intl.formatMessage(isExpanded ? messages.collapseGroup : messages.expandGroup)}
          />
          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
        </Button>
      </FlexRow>
    </div>
  );
};
